import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * @typedef {object} LocalizationGlassRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLDivElement} panel
 * @property {HTMLButtonElement} [closeButton]
 * @property {HTMLParagraphElement} title
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} countryInput
 * @property {HTMLInputElement} languageInput
 * @property {HTMLSpanElement} [currentCode]
 * @property {HTMLSpanElement} [currentFlag]
 * @property {HTMLDivElement} [countryStep]
 * @property {HTMLDivElement} [languageStep]
 * @property {HTMLUListElement} [languageList]
 */

/**
 * @typedef {{ iso_code: string, label: string, flag_url: string }} LocalizationGlassLanguage
 */

/**
 * @typedef {object} LocalizationGlassData
 * @property {boolean} twoStep
 * @property {boolean} showCountry
 * @property {boolean} showLanguage
 * @property {string} currentCountry
 * @property {string} currentLanguage
 * @property {boolean} startOnLanguage
 * @property {number} singleCountry
 * @property {Record<string, LocalizationGlassLanguage[]>} countryLanguages
 */

/**
 * @extends {Component<LocalizationGlassRefs>}
 */
class LocalizationGlassComponent extends Component {
  /** @type {LocalizationGlassData | null} */
  #data = null;

  /** @type {string | null} */
  #selectedCountry = null;

  connectedCallback() {
    super.connectedCallback();
    this.#loadData();

    if (this.isDrawer) {
      this.#drawerDetails = this.closest('.menu-drawer-container');
      this.#drawerDetails?.addEventListener('toggle', this.#handleDrawerToggle);
      return;
    }

    this.#searchModal = document.getElementById('search-modal');
    this.#searchModal?.addEventListener('dialog:open', this.hidePanel);
    this.#cartDrawer = document.querySelector('cart-drawer-component.cart-drawer--sana');
    this.#cartDrawer?.addEventListener('dialog:open', this.#handleCartDialogOpen);
    document.addEventListener('click', this.#handleHeaderChromeClick, true);
  }

  disconnectedCallback() {
    this.#drawerDetails?.removeEventListener('toggle', this.#handleDrawerToggle);
    this.#searchModal?.removeEventListener('dialog:open', this.hidePanel);
    this.#cartDrawer?.removeEventListener('dialog:open', this.#handleCartDialogOpen);
    document.removeEventListener('click', this.#handleHeaderChromeClick, true);
    super.disconnectedCallback();
  }

  /** @type {HTMLDetailsElement | null} */
  #drawerDetails = null;

  /** @type {HTMLElement | null} */
  #searchModal = null;

  /** @type {HTMLElement | null} */
  #cartDrawer = null;

  #handleDrawerToggle = () => {
    if (!this.#drawerDetails?.open) {
      this.hidePanel();
    }
  };

  /**
   * Close header localization when interacting with search/cart chrome.
   * @param {Event} event
   */
  #handleHeaderChromeClick = (event) => {
    if (this.isDrawer || this.isHidden) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const cartTrigger = target.closest(
      'cart-drawer-component [data-testid="cart-drawer-trigger"], .action__cart'
    );
    const searchTrigger = target.closest('.search-action, .search-modal__button');

    if (cartTrigger || searchTrigger) {
      this.hidePanel();
    }
  };

  /**
   * @param {Event} event
   */
  #handleCartDialogOpen = () => {
    if (!this.isDrawer) {
      this.hidePanel();
    }
  };

  get isHidden() {
    return !this.refs.panel || this.refs.panel.hasAttribute('hidden');
  }

  get isDrawer() {
    return this.dataset.variant === 'drawer';
  }

  #loadData() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script?.textContent) return;

    try {
      this.#data = JSON.parse(script.textContent);
    } catch {
      this.#data = null;
    }
  }

  #setStep(step) {
    const { countryStep, languageStep, title } = this.refs;
    const titleCountry = title?.dataset.titleCountry ?? '';
    const titleLanguage = title?.dataset.titleLanguage ?? '';

    if (step === 'country') {
      countryStep?.removeAttribute('hidden');
      languageStep?.setAttribute('hidden', '');
      if (title) title.textContent = titleCountry;
      this.#updateCurrentBadge(this.#selectedCountry ?? this.#data?.currentCountry ?? '', 'country');
      return;
    }

    countryStep?.setAttribute('hidden', '');
    languageStep?.removeAttribute('hidden');
    if (title) title.textContent = titleLanguage;
    this.#updateCurrentBadge(this.refs.languageInput?.value ?? this.#data?.currentLanguage ?? '', 'language');
  }

  /**
   * @param {string} iso
   * @param {'country' | 'language'} type
   */
  #updateCurrentBadge(iso, type) {
    if (!this.isDrawer || !this.#data) return;

    const { currentCode, currentFlag } = this.refs;
    if (!currentCode) return;

    if (type === 'country') {
      const countryIso = iso || this.#data.currentCountry;
      currentCode.textContent = countryIso.toUpperCase();
      const countryButton = this.querySelector(`[data-country="${countryIso}"] .localization-glass-panel__flag`);
      if (currentFlag && countryButton instanceof HTMLElement) {
        currentFlag.style.backgroundImage = countryButton.style.backgroundImage;
      }
      return;
    }

    const countryIso = this.#selectedCountry ?? this.#data.currentCountry;
    const languages = this.#data.countryLanguages[countryIso] ?? [];
    const language = languages.find((entry) => entry.iso_code === iso);
    currentCode.textContent = language?.label ?? iso.split('-')[0].toUpperCase();
    if (currentFlag && language?.flag_url) {
      currentFlag.style.backgroundImage = `url(${language.flag_url})`;
    }
  }

  #resetPanel() {
    this.#selectedCountry = null;
    if (!this.#data) return;

    if (this.#data.startOnLanguage) {
      this.#setStep('language');
      this.#renderLanguages(this.#data.currentCountry);
      return;
    }

    if (this.#data.twoStep || this.#data.showCountry) {
      this.#setStep('country');
    }
  }

  /**
   * @param {string} countryIso
   */
  #renderLanguages(countryIso) {
    const { languageList, languageInput } = this.refs;
    if (!languageList || !this.#data) return;

    const languages = this.#data.countryLanguages[countryIso] ?? [];
    const currentLanguage = languageInput?.value ?? this.#data.currentLanguage;

    languageList.replaceChildren();

    for (const language of languages) {
      const item = document.createElement('li');
      item.className = 'localization-glass-panel__grid-item';

      const isActive = language.iso_code === currentLanguage;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `localization-glass-panel__option${isActive ? ' localization-glass-panel__option--active' : ''}`;
      button.dataset.language = language.iso_code;
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
      button.addEventListener('click', () => this.selectLanguage(language.iso_code));

      const flag = document.createElement('span');
      flag.className = 'localization-glass-panel__flag icon-flag';
      flag.style.backgroundImage = `url(${language.flag_url})`;
      flag.setAttribute('aria-hidden', 'true');

      const code = document.createElement('span');
      code.className = 'localization-glass-panel__code';
      code.textContent = language.label;

      button.append(flag, code);
      item.append(button);
      languageList.append(item);
    }
  }

  toggleSelector(event) {
    if (event instanceof Event) {
      event.preventDefault();
      event.stopPropagation();
    }

    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  showPanel() {
    if (!this.refs.panel || !this.isHidden) return;

    this.#resetPanel();
    this.refs.panel.removeAttribute('hidden');
    this.refs.button?.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');

    if (!this.isDrawer) {
      this.addEventListener('keyup', this.#handleKeyUp);
      document.addEventListener('click', this.#handleClickOutside);
    } else {
      // Close language panel when navigating the drawer menu (avoids stealing the first tap).
      this.#drawerDetails?.querySelector('menu-drawer-glass')?.addEventListener(
        'click',
        this.#handleDrawerMenuClick,
        true
      );
      requestAnimationFrame(() => {
        this.refs.panel?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }

    if (this.#data?.startOnLanguage) {
      this.#setStep('language');
      this.#renderLanguages(this.#data.currentCountry);
    }
  }

  hidePanel = () => {
    if (!this.refs.panel || this.isHidden) return;

    if (!this.isDrawer) {
      this.removeEventListener('keyup', this.#handleKeyUp);
      document.removeEventListener('click', this.#handleClickOutside);
    } else {
      this.#drawerDetails?.querySelector('menu-drawer-glass')?.removeEventListener(
        'click',
        this.#handleDrawerMenuClick,
        true
      );
    }

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
    this.classList.remove('is-open');
    this.#resetPanel();

    // Do not focus the trigger in the drawer — on mobile that steals the next tap
    // and re-opens the country/language grid instead of the menu item.
    if (!this.isDrawer) {
      this.refs.button?.focus();
    }
  };

  /**
   * While the language panel is open, any menu navigation click closes it first.
   * @param {Event} event
   */
  #handleDrawerMenuClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('menu-drawer-localization-glass-component')) return;

    this.hidePanel();
  };

  /**
   * @param {string} countryIso
   */
  selectCountry(countryIso) {
    const { countryInput, form } = this.refs;
    if (!countryInput || !this.#data) return;

    countryInput.value = countryIso;
    this.#selectedCountry = countryIso;
    this.#updateCurrentBadge(countryIso, 'country');

    if (this.#data.twoStep || this.#data.showLanguage) {
      const languages = this.#data.countryLanguages[countryIso] ?? [];

      if (languages.length === 1) {
        this.selectLanguage(languages[0].iso_code);
        return;
      }

      this.#renderLanguages(countryIso);
      this.#setStep('language');
      return;
    }

    form?.submit();
  }

  /**
   * @param {string} languageIso
   */
  selectLanguage(languageIso) {
    const { languageInput, form } = this.refs;
    if (!languageInput) return;

    languageInput.value = languageIso;
    form?.submit();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.hidePanel();
      event.stopPropagation();
      this.refs.button?.focus();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };
}

class HeaderLocalizationGlassComponent extends LocalizationGlassComponent {}

class MenuDrawerLocalizationGlassComponent extends LocalizationGlassComponent {}

if (!customElements.get('header-localization-glass-component')) {
  customElements.define('header-localization-glass-component', HeaderLocalizationGlassComponent);
}

if (!customElements.get('menu-drawer-localization-glass-component')) {
  customElements.define('menu-drawer-localization-glass-component', MenuDrawerLocalizationGlassComponent);
}
